import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Seo from '@/components/Seo';
import { motion } from 'framer-motion';
import { ArrowLeft, ShoppingBag, ExternalLink, Star, BookOpen, Check, Play, X } from 'lucide-react';
import { getBook, addToCart } from '@/lib/api';
import { toast } from 'sonner';
import PayPalBuy from '@/components/PayPalBuy';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BookDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [format, setFormat] = useState('digital');
  const [selectedFormatName, setSelectedFormatName] = useState('');
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const data = await getBook(id);
        if (data.slug && data.slug !== id) navigate(`/books/${data.slug}`, { replace: true });
        setBook(data);
        if (data.formats && data.formats.length > 0) {
          setSelectedFormatName(data.formats[0].name);
        }
      } catch (error) {
        console.error('Error fetching book:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchBook();
  }, [id]);

  const handleAddToCart = async () => {
    setIsAddingToCart(true);
    try {
      await addToCart(book.id, 1, format);
      toast.success('Added to cart!');
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setIsAddingToCart(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div className="aspect-[2/3] bg-white/5 img-loading" />
            <div className="space-y-6">
              <div className="h-4 bg-white/5 w-1/4 img-loading" />
              <div className="h-10 bg-white/5 w-3/4 img-loading" />
              <div className="h-24 bg-white/5 img-loading" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center">
        <Seo title="Book Not Found" noindex />
        <div className="text-center">
          <p className="text-white/60 mb-4">Book not found</p>
          <Link to="/books" className="text-gold hover:text-gold/80 transition-colors">
            Return to Bookstore
          </Link>
        </div>
      </div>
    );
  }

  const externalLinks = [
    { key: 'amazon', label: 'Amazon', icon: ExternalLink },
    { key: 'barnes_noble', label: 'Barnes & Noble', icon: ExternalLink },
    { key: 'bookshop', label: 'Bookshop.org', icon: ExternalLink },
    { key: 'indie_bound', label: 'IndieBound', icon: ExternalLink },
  ];

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="book-detail-page">
      <Seo
        title={`${book.title} by ${book.author}`}
        description={(book.short_description || book.synopsis || '').slice(0, 160)}
        image={book.cover_image}
        type="book"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Book',
          name: book.title,
          author: { '@type': 'Person', name: book.author },
          publisher: { '@type': 'Organization', name: 'Ink & Innovation Printworks' },
          image: book.cover_image,
          description: book.short_description || book.synopsis,
          genre: book.genre,
          isbn: book.isbn || undefined,
        }}
      />
      {/* Video Trailer Modal */}
      {showVideo && book.video_url && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setShowVideo(false)}
          data-testid="video-modal"
        >
          <button
            onClick={() => setShowVideo(false)}
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"
            aria-label="Close video"
            data-testid="close-video-btn"
          >
            <X size={32} />
          </button>
          <div className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <video
              src={book.video_url}
              className="w-full rounded-lg shadow-2xl"
              controls
              autoPlay
              playsInline
              data-testid="trailer-video"
            />
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Back Link */}
        <Link
          to="/books"
          className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-12"
          data-testid="back-to-books"
        >
          <ArrowLeft size={16} />
          <span className="text-sm uppercase tracking-widest">Back to Bookstore</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Book Cover */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="relative">
              <img
                src={book.cover_image}
                alt={book.title}
                className="w-full max-w-md mx-auto lg:mx-0 aspect-[2/3] object-cover gold-glow"
                data-testid="book-cover"
              />
              {book.video_url && (
                <button
                  onClick={() => setShowVideo(true)}
                  className="absolute inset-0 max-w-md mx-auto lg:mx-0 flex items-center justify-center group"
                  aria-label="Play trailer"
                  data-testid="play-trailer-btn"
                >
                  <span className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors duration-300" />
                  <span className="relative flex items-center justify-center w-20 h-20 rounded-full bg-gold/90 text-[#050505] group-hover:scale-110 transition-transform duration-300 shadow-2xl">
                    <Play size={30} className="ml-1 fill-current" />
                  </span>
                  <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs uppercase tracking-widest text-white/90">
                    Watch the Trailer
                  </span>
                </button>
              )}
            </div>
          </motion.div>

          {/* Book Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-8"
          >
            <div>
              <p className="mono text-xs text-white/40 mb-3">{book.genre}</p>
              {book.series && (
                <p className="text-gold text-sm uppercase tracking-widest mb-3" data-testid="book-series">
                  {book.series}
                </p>
              )}
              <h1 className="text-3xl lg:text-4xl font-semibold mb-3" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="book-title">
                {book.title}
              </h1>
              <p className="text-lg text-white/60">by {book.author}</p>
              {book.short_description && (
                <p className="text-white/70 mt-4 leading-relaxed" data-testid="book-short-description">
                  {book.short_description}
                </p>
              )}
            </div>

            {/* Purchase Section */}
            {book.purchase_type === 'paypal' ? (
              <PayPalBuy
                book={book}
                selectedFormat={selectedFormatName}
                onSelectFormat={setSelectedFormatName}
              />
            ) : (
              <div className="space-y-4">
                <p className="mono text-2xl text-gold" data-testid="book-price">${book.price?.toFixed(2)}</p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Select value={format} onValueChange={setFormat}>
                    <SelectTrigger className="w-full sm:w-[180px] bg-transparent border-white/20 rounded-none" data-testid="format-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#101010] border-white/10">
                      {book.is_digital && (
                        <SelectItem value="digital" className="text-white/80 focus:bg-white/10 focus:text-white">
                          Digital (eBook)
                        </SelectItem>
                      )}
                      {book.is_physical && (
                        <SelectItem value="physical" className="text-white/80 focus:bg-white/10 focus:text-white">
                          Paperback
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>

                  <button
                    onClick={handleAddToCart}
                    disabled={isAddingToCart}
                    className="btn-primary flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="add-to-cart-btn"
                  >
                    {isAddingToCart ? (
                      <>Adding...</>
                    ) : (
                      <>
                        <ShoppingBag size={18} />
                        Add to Cart
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* External Purchase Links */}
            {book.external_links && Object.keys(book.external_links).length > 0 && (
              <div className="pt-4 border-t border-white/10">
                <p className="text-sm text-white/40 mb-4">Also available at:</p>
                <div className="flex flex-wrap gap-3">
                  {externalLinks.map(({ key, label, icon: Icon }) => 
                    book.external_links[key] && (
                      <a
                        key={key}
                        href={book.external_links[key]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-white/10 hover:border-white/30 text-white/60 hover:text-white text-sm transition-colors"
                        data-testid={`external-link-${key}`}
                      >
                        {label}
                        <Icon size={12} />
                      </a>
                    )
                  )}
                </div>
              </div>
            )}

            {/* Book Meta */}
            {book.purchase_type !== 'paypal' && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-6 border-t border-b border-white/10">
                {book.pages && (
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Pages</p>
                    <p className="mono text-sm">{book.pages}</p>
                  </div>
                )}
                {book.published_date && (
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Published</p>
                    <p className="mono text-sm">{new Date(book.published_date).getFullYear()}</p>
                  </div>
                )}
                {book.isbn && (
                  <div className="col-span-2">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-1">ISBN</p>
                    <p className="mono text-sm">{book.isbn}</p>
                  </div>
                )}
              </div>
            )}

            {/* Product Details (per selected format) */}
            {(() => {
              const activeFmt = book.formats?.find((f) => f.name === selectedFormatName);
              const details = activeFmt?.details || book.product_details || {};
              if (Object.keys(details).length === 0) return null;
              return (
                <div className="pt-2" data-testid="product-details">
                  <p className="text-sm text-white/40 mb-4 uppercase tracking-widest">
                    Product Details{activeFmt ? ` — ${activeFmt.name}` : ''}
                  </p>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                    {[
                      ['Print length', details.print_length],
                      ['Language', details.language],
                      ['Publisher', details.publisher],
                      ['Publication date', details.publication_date],
                      ['Dimensions', details.dimensions],
                      ['ISBN', details.isbn],
                    ].map(([label, value]) =>
                      value ? (
                        <div key={label} className="flex justify-between gap-4 border-b border-white/5 pb-2">
                          <dt className="text-white/40 text-sm">{label}</dt>
                          <dd className="text-white/80 text-sm text-right">{value}</dd>
                        </div>
                      ) : null
                    )}
                  </dl>
                </div>
              );
            })()}
          </motion.div>
        </div>

        {/* Tabs: Synopsis, Excerpt, Reviews */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-16"
        >
          <Tabs defaultValue="synopsis" className="w-full">
            <TabsList className="bg-transparent border-b border-white/10 w-full justify-start gap-8 rounded-none p-0">
              <TabsTrigger
                value="synopsis"
                className="bg-transparent data-[state=active]:bg-transparent data-[state=active]:text-gold text-white/50 border-b-2 border-transparent data-[state=active]:border-gold rounded-none px-0 pb-4"
                data-testid="tab-synopsis"
              >
                Synopsis
              </TabsTrigger>
              {book.excerpt && (
                <TabsTrigger
                  value="excerpt"
                  className="bg-transparent data-[state=active]:bg-transparent data-[state=active]:text-gold text-white/50 border-b-2 border-transparent data-[state=active]:border-gold rounded-none px-0 pb-4"
                  data-testid="tab-excerpt"
                >
                  Excerpt
                </TabsTrigger>
              )}
              {book.reviews && book.reviews.length > 0 && (
                <TabsTrigger
                  value="reviews"
                  className="bg-transparent data-[state=active]:bg-transparent data-[state=active]:text-gold text-white/50 border-b-2 border-transparent data-[state=active]:border-gold rounded-none px-0 pb-4"
                  data-testid="tab-reviews"
                >
                  Reviews
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="synopsis" className="mt-8">
              <p className="text-white/70 max-w-3xl leading-relaxed whitespace-pre-line" data-testid="book-synopsis">
                {book.synopsis}
              </p>
            </TabsContent>

            {book.excerpt && (
              <TabsContent value="excerpt" className="mt-8">
                <div className="card-surface p-8 lg:p-12 max-w-3xl">
                  <BookOpen size={24} className="text-gold mb-6" />
                  <p className="text-white/70 leading-relaxed italic" data-testid="book-excerpt">
                    "{book.excerpt}"
                  </p>
                </div>
              </TabsContent>
            )}

            {book.reviews && book.reviews.length > 0 && (
              <TabsContent value="reviews" className="mt-8">
                <div className="space-y-6 max-w-3xl">
                  {book.reviews.map((review, index) => (
                    <div key={index} className="card-surface p-6" data-testid={`review-${index}`}>
                      <div className="flex items-center gap-2 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={14}
                            className={i < review.rating ? 'text-gold fill-gold' : 'text-white/20'}
                          />
                        ))}
                      </div>
                      {review.title && (
                        <h4 className="text-white font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                          {review.title}
                        </h4>
                      )}
                      <p className="text-white/70 italic mb-4">"{review.text}"</p>
                      <p className="text-white/40 text-sm">
                        — {review.reviewer}
                        {review.location && <span className="text-white/40">, {review.location}</span>}
                        {(review.format || review.date) && (
                          <span className="text-white/30">
                            {' · '}
                            {[review.format, review.date].filter(Boolean).join(' · ')}
                          </span>
                        )}
                        {review.verified && (
                          <span className="text-gold/70 ml-2">✓ {review.verified_label || 'Verified Purchase'}</span>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            )}
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
};

export default BookDetailPage;
